export type CrmActionHrefKind = "internal" | "external" | "phone" | "sms" | "email";

const CONTROL_OR_SPACE_RE = /[\u0000-\u001f\u007f\s]/;
const PHONE_TARGET_RE = /^\+?[0-9*#;,().\-\s]{3,80}$/;
const SMS_TARGET_RE = /^\+?[0-9().\-\s]{3,80}$/;
const EMAIL_TARGET_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i;
const MAILTO_QUERY_RE = /^[A-Z0-9._~!$&'()*+,;=:@/?%-]*$/i;

export function normalizeCrmActionHref(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("/") && !trimmed.startsWith("//") && !CONTROL_OR_SPACE_RE.test(trimmed)) {
    return trimmed;
  }

  const colonIndex = trimmed.indexOf(":");
  if (colonIndex <= 0) return null;
  const protocol = trimmed.slice(0, colonIndex).toLowerCase();

  if (protocol === "https") {
    return normalizeHttpsHref(trimmed);
  }
  if (protocol === "tel") {
    return normalizeTelHref(trimmed.slice(colonIndex + 1));
  }
  if (protocol === "sms") {
    return normalizeSmsHref(trimmed.slice(colonIndex + 1));
  }
  if (protocol === "mailto") {
    return normalizeMailtoHref(trimmed.slice(colonIndex + 1));
  }

  return null;
}

export function getCrmActionHrefKind(href: string): CrmActionHrefKind | null {
  if (href.startsWith("/") && !href.startsWith("//")) return "internal";
  const colonIndex = href.indexOf(":");
  if (colonIndex <= 0) return null;
  const protocol = href.slice(0, colonIndex).toLowerCase();
  if (protocol === "https") return "external";
  if (protocol === "tel") return "phone";
  if (protocol === "sms") return "sms";
  if (protocol === "mailto") return "email";
  return null;
}

export function shouldOpenCrmActionHrefInNewTab(href: string): boolean {
  return getCrmActionHrefKind(href) === "external";
}

function normalizeHttpsHref(value: string): string | null {
  if (CONTROL_OR_SPACE_RE.test(value)) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

function normalizeTelHref(value: string): string | null {
  const target = value.trim();
  if (!target || /%0d|%0a/i.test(target) || !PHONE_TARGET_RE.test(target)) return null;
  const compact = target.replace(/[\s().-]/g, "");
  if (!/[0-9]/.test(compact)) return null;
  return `tel:${target.replace(/\s+/g, "")}`;
}

function normalizeSmsHref(value: string): string | null {
  const target = value.trim();
  if (!target || /%0d|%0a|\?|&/i.test(target) || !SMS_TARGET_RE.test(target)) return null;
  const compact = target.replace(/[\s().-]/g, "");
  if (!/[0-9]/.test(compact)) return null;
  return `sms:${target.replace(/\s+/g, "")}`;
}

function normalizeMailtoHref(value: string): string | null {
  const target = value.trim();
  if (!target || /%0d|%0a|[\r\n]/i.test(target)) return null;
  const [email = "", query = ""] = target.split("?", 2);
  if (!EMAIL_TARGET_RE.test(email)) return null;
  if (query && !MAILTO_QUERY_RE.test(query)) return null;
  return `mailto:${target}`;
}
